
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { Constants, GlobalSettings } from '../../common';
import { MatInputModule } from '@angular/material/input';
import { AuthService, CommonService, ToasterService } from '../../services';
import { IAccountDeleteData, IResponseMessage } from '../../models';
import { HttpErrorResponse } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-delete-account',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatProgressBarModule
  ],
  templateUrl: './delete-account.component.html',
  styleUrl: './delete-account.component.scss'
})
export class DeleteAccountComponent {

  currentStep = 0;

  hidePassword = true;
  isVerifyPasswordLoading = false;
  isVerifyCodeApiLoading = false;
  isResendCode = false;
  isTimerRunning = false;
  accountDeletionInProgress = false;
  accountDeleted = false;

  deletionProgress: number = 0;      // Current progress (0-100)
  progressInterval: any;

  originalCountDown = Constants.resendCodeTime;
  countdown: number = Constants.resendCodeTime;

  confirmUserPasswordForm!: FormGroup;
  verifyCodeForm!: FormGroup;

  limits = GlobalSettings.characterLimits;

  constructor(private dialogRef: MatDialogRef<DeleteAccountComponent>, private authService: AuthService, private fb: FormBuilder, private commonService: CommonService, private toaster: ToasterService) {
    this.initializeForms();
  }

  initializeForms() {
    const profileDetail = this.commonService.profileDetail;
    this.confirmUserPasswordForm = this.fb.group({
      full_name: [profileDetail.full_name, Validators.required],
      email: [profileDetail.email, [Validators.required, Validators.email, Validators.pattern(Constants.emailPattern)]],
      password: ['', [Validators.required, Validators.pattern(Constants.passwordPattern)]]
    });
    this.confirmUserPasswordForm.get('full_name')?.disable();
    this.confirmUserPasswordForm.get('email')?.disable();

    this.verifyCodeForm = this.fb.group({
      verification_code: ['', Validators.required],
      email: [profileDetail.email, [Validators.required, Validators.email, Validators.pattern(Constants.emailPattern)]]
    })
  }

  getPasswordErrorMessage(): string {
    const password = this.confirmUserPasswordForm.get('password')?.value || '';
    if (!password) {
      return 'Password is required';
    }

    let errors = [];
    if (password.length < 8) errors.push('8 characters long');
    if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('one number');
    if (!/[^A-Za-z0-9]/.test(password)) errors.push('one special character');

    if (errors.length === 0) return '';

    if (errors.length === 1) {
      if (password.length < 8) return 'Password must be at least 8 characters long.';
      return `Password must include at least ${errors[0]}.`;
    }

    if (password.length < 8) {
      errors.shift();
      if (errors.length === 1) {
        return `Password must be at least 8 characters long and include at least ${errors[0]}.`;
      }
      const last = errors.pop();
      return `Password must be at least 8 characters long and include at least ${errors.join(', ')}, and ${last}.`;
    } else {
      const last = errors.pop();
      return `Password must include at least ${errors.join(', ')}, and ${last}.`;
    }
  }

  getUserEmail() {
    return this.commonService?.profileDetail?.email ? this.commonService.profileDetail.email : '';
  }

  nextStep() {
    if (this.commonService.profileDetail.has_password) {
      this.currentStep++;
    }
    else {
      this.commonService.isLoading = true;
      this.isVerifyPasswordLoading = true;
      const accountDeleteData: IAccountDeleteData = {} as IAccountDeleteData;
      accountDeleteData.email = this.commonService.profileDetail.email;
      accountDeleteData.full_name = this.commonService.profileDetail.full_name;
      this.accountVerifyPassword(accountDeleteData);
    }
  }

  prevStep() {
    if (this.currentStep > 0) this.currentStep--;
  }

  closeDialog(): void {
    this.dialogRef.close();
    this.currentStep = 0;
  }

  verifyPassword() {
    if (this.confirmUserPasswordForm.valid) {
      this.commonService.isLoading = true;
      this.isVerifyPasswordLoading = true;
      const accountDeleteData: IAccountDeleteData = {} as IAccountDeleteData;
      accountDeleteData.email = this.commonService.profileDetail.email;
      accountDeleteData.full_name = this.commonService.profileDetail.full_name;
      accountDeleteData.password = this.confirmUserPasswordForm.value.password;
      this.accountVerifyPassword(accountDeleteData);
    }
    else {
      if (!this.confirmUserPasswordForm.valid) {
        this.toaster.warning(Constants.notifyMessages.invalidForm);
      }
      Object.values(this.confirmUserPasswordForm.controls).forEach(control => {
        control.markAsTouched();
      });
    }
  }

  accountVerifyPassword(accountDeleteData: IAccountDeleteData) {
    this.authService.accountVerifyPassword(accountDeleteData).subscribe({
      next: (data: IResponseMessage) => {
        this.commonService.isLoading = false;
        // console.log(data);
        this.currentStep = 2;
        this.startCountdown();
        this.isVerifyPasswordLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.commonErrorLoading);
        this.commonService.isLoading = false;
        this.isVerifyPasswordLoading = false;
      }
    })
  }

  startCountdown() {
    this.isTimerRunning = false;
    this.countdown = Constants.resendCodeTime;
    const interval = setInterval(() => {
      this.isTimerRunning = true;
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        clearInterval(interval);
        this.isTimerRunning = false;
      }
    }, 1000);
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${minutes}:${sec < 10 ? '0' : ''}${sec}`;
  }

  getCountDownTime() {
    return this.formatTime(this.countdown);
  }

  verifyCode() {
    if (this.verifyCodeForm.valid) {
      this.isVerifyCodeApiLoading = true;
      this.authService.accountVerifyCode(this.verifyCodeForm.value).subscribe({
        next: (data: IResponseMessage) => {
          // console.log(data);
          this.isVerifyCodeApiLoading = false;
          this.currentStep = 3;
        },
        error: (error: HttpErrorResponse) => {
          this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.commonErrorLoading);
          this.isResendCode = false;
          this.isVerifyCodeApiLoading = false;
          if (this.commonService.profileDetail.has_password) {
            this.currentStep = 1;
          }
          console.log(error);
        }
      });
    }
    else {
      Object.values(this.verifyCodeForm.controls).forEach(control => {
        control.markAsTouched();
      });
    }
  }

  resendCode() {
    this.verifyPassword();
  }

  deleteAccount() {
    this.commonService.isLoading = true;
    this.accountDeletionInProgress = true;
    this.deletionProgress = 0;
    this.accountDeleted = false;

    // Animate progress bar up to 90% while waiting for API response
    const maxProgressBeforeComplete = 90;
    this.progressInterval = setInterval(() => {
      if (this.deletionProgress < maxProgressBeforeComplete) {
        this.deletionProgress += 2; // Speed can be adjusted
      }
    }, 80);

    this.authService.accountDelete().subscribe({
      next: (data: IResponseMessage) => {
        // API succeeded: complete animation
        clearInterval(this.progressInterval);
        this.deletionProgress = 100;
        setTimeout(() => {
          this.accountDeletionInProgress = false;
          this.accountDeleted = true;
          this.commonService.isLoading = false;
          // Optionally redirect after a few seconds
          setTimeout(() => {
            this.closeDialog();
            this.authService.logout();
          }, 10000);
        }, 800); // brief pause so user sees 100%
      },
      error: (error: HttpErrorResponse) => {
        // API failed: complete animation and show deleted screen (update as needed)
        clearInterval(this.progressInterval);
        this.deletionProgress = 100;
        setTimeout(() => {
          this.accountDeletionInProgress = false;
          this.accountDeleted = false;
          this.commonService.isLoading = false;
          this.currentStep = 1;
          this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.commonErrorLoading);
          // Optionally handle error state separately
        }, 800);
      }
    });
  }


}
